import { CompositeLayer, CompositeLayerProps, Layer } from "@deck.gl/core"
import { VideoMetadata } from "@nova/slice-video"
import { ColorFinderLayerState } from "./frameBbox"
import { IconLayer } from "@deck.gl/layers"
import { subscribeToFootageEvent } from "plugins/ColorFinder/V2/ColorFinderEvents"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { useColorFinderMetadata } from "plugins/ColorFinder/V2/ColorFinderQueries"
import {
    selectColorFinderFlightId,
    selectColorFinderIsOpen,
} from "plugins/ColorFinder/V2/redux/selectors"
import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"

export const useCFDroneIconLayer = () => {
    const isOpen = useAppSelector(selectColorFinderIsOpen)
    const flightId = useAppSelector(selectColorFinderFlightId)
    const flight = useAppSelector(selectColorFinderFlightById(flightId))
    const { data: metadata } = useColorFinderMetadata(flight)

    const layer = useMemo(() => {
        if (!flightId || !isOpen || !metadata) {
            return null
        }

        const l = new ColorFinderDroneIconLayer({
            id: `${flightId}-drone-icon`,
            metadata: metadata,
            iconMapping: { drone: { x: 0, y: 0, width: 128, height: 128 } },
            sizeScale: 50,
        })

        return l
    }, [flightId, isOpen, metadata])

    return (layer ? [layer] : []) as unknown as Layer[] // if you know the secret deckgl typescript passcode this doesn't need to exist
}

export type ColorFinderDroneIconLayerProps = CompositeLayerProps & {
    metadata: VideoMetadata | null
    iconMapping: object
    sizeScale: number
}

export class ColorFinderDroneIconLayer
    extends CompositeLayer<ColorFinderDroneIconLayerProps>
    implements Layer<ColorFinderDroneIconLayerProps>
{
    initializeState() {
        this.setState({
            droneFrameData: undefined,
            unsubscribe: () => {},
        })
        this.subscribe()
    }

    updateState({
        props,
        oldProps,
    }: {
        props: ColorFinderDroneIconLayerProps
        oldProps: ColorFinderDroneIconLayerProps
    }) {
        // Re-subscribe if metadata or the subscription function changes
        if (props.metadata !== oldProps.metadata) {
            ;(this.state as ColorFinderLayerState).unsubscribe()
            this.subscribe()
        }
    }

    finalizeState() {
        // Clean up the event listener
        ;(this.state as ColorFinderLayerState).unsubscribe()
    }

    subscribe() {
        const { metadata } = this.props
        if (!metadata) {
            return
        }

        const unsubscribe = subscribeToFootageEvent((event) => {
            const frame = this.props.metadata?.[event.frame]
            if (frame) {
                // Update internal state, which triggers renderLayers
                this.setState({ droneFrameData: frame })
            }
        })

        this.setState({ unsubscribe })
    }

    renderLayers() {
        const { id, sizeScale } = this.props
        const { droneFrameData } = this.state

        if (!droneFrameData) {
            return null // Don't render anything if we have no data
        }

        // Render the IconLayer with the latest data from state
        return new IconLayer({
            id: `${id}-icon`,
            data: [droneFrameData], // Data is an array with just our one item
            getIcon: (d) =>
                d[3] || d[4] || d[5]
                    ? {
                          url: `/images/drone-video-icon.svg`,
                          width: 512,
                          height: 512,
                      }
                    : {
                          url: `/images/drone-video-icon-basic.svg`,
                          width: 512,
                          height: 512,
                      },
            sizeScale,
            sizeUnits: "pixels",
            getPosition: (d) => d.slice(0, 3),
            getAngle: (d) => -d[4],
            billboard: false,
            getSize: 0.5,
        })
    }
}
